import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './KYCVerification.css';

function KYCVerification() {
  const [formData, setFormData] = useState({
    document_type: 'passport',
    document_number: '',
  });
  const [files, setFiles] = useState({
    document_front: null,
    document_back: null,
    selfie: null
  });
  const [filePreviews, setFilePreviews] = useState({
    document_front: null,
    document_back: null,
    selfie: null
  });
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchKYCStatus();
  }, []);

  const fetchKYCStatus = async () => {
    try {
      const response = await axios.get('https://trcsite.net/api/kyc/status');
      setKycStatus(response.data.kyc_application);
    } catch (error) {
      console.error('Error fetching KYC status:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      console.log('File selected:', fieldName, file.name, file.size, file.type);
      
      // Validate file type and size
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file (JPEG, PNG, etc.)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB
        setError(`File ${file.name} is too large. Maximum size is 5MB.`);
        return;
      }
      
      // Create preview
      const previewUrl = URL.createObjectURL(file);
      setFilePreviews(prev => ({
        ...prev,
        [fieldName]: previewUrl
      }));

      // Store file for submission
      setFiles(prev => ({
        ...prev,
        [fieldName]: file
      }));
      
      setError('');
    } else {
      console.log('No file selected for:', fieldName);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    console.log('Submitting KYC form...');
    console.log('Form data:', formData);
    console.log('Files:', files);

    // Validate required files
    if (!files.document_front || !files.selfie) {
      const errorMsg = 'Document front and selfie are required';
      console.log('Validation error:', errorMsg);
      setError(errorMsg);
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('document_type', formData.document_type);
      submitData.append('document_number', formData.document_number);
      submitData.append('document_front', files.document_front);
      submitData.append('selfie', files.selfie);
      
      if (files.document_back) {
        submitData.append('document_back', files.document_back);
      }

      // Log FormData contents
      console.log('FormData entries:');
      for (let [key, value] of submitData.entries()) {
        console.log(key, value instanceof File ? `${value.name} (${value.size} bytes)` : value);
      }

      const response = await axios.post('https://trcsite.net/api/kyc/submit', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      console.log('Submission successful:', response.data);
      
      setMessage('KYC application submitted successfully!');
      setFiles({
        document_front: null,
        document_back: null,
        selfie: null
      });
      setFilePreviews({
        document_front: null,
        document_back: null,
        selfie: null
      });
      
      // Clear file inputs
      document.querySelectorAll('input[type="file"]').forEach(input => {
        input.value = '';
      });
      
      fetchKYCStatus();
    } catch (error) {
      console.error('Submission error details:', error);
      console.error('Response:', error.response);
      
      let errorMessage = 'Failed to submit KYC application';
      
      if (error.response) {
        // Server responded with error status
        errorMessage = error.response.data?.error || errorMessage;
        if (error.response.data?.details) {
          errorMessage += `: ${error.response.data.details}`;
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        // Something else happened
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // File Preview Component
  const FilePreview = ({ src, alt }) => (
    src && (
      <div style={{ marginTop: '10px' }}>
        <img 
          src={src} 
          alt={alt}
          style={{
            maxWidth: '200px',
            maxHeight: '150px',
            border: '1px solid #344258',
            borderRadius: '4px'
          }}
        />
        <div style={{ fontSize: '12px', color: '#b0b8c4', marginTop: '5px' }}>
          Preview
        </div>
      </div>
    )
  );

  if (kycStatus) {
    return (
      <div className="auth-container">
        <div className="auth-box-kyc">
          <div className="auth-header">
            <h1>KYC Verification</h1>
            <p>Your KYC application status</p>
          </div>
          
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ 
              fontSize: '18px', 
              fontWeight: 'bold',
              color: kycStatus.status === 'approved' ? '#00c853' : 
                     kycStatus.status === 'rejected' ? '#ff1744' : '#2962ff',
              marginBottom: '10px'
            }}>
              Status: {kycStatus.status.toUpperCase()}
            </div>
            
            {kycStatus.status === 'pending' && (
              <p>Your KYC application is under review. We'll notify you once it's processed.</p>
            )}
            
            {kycStatus.status === 'approved' && (
              <p>Your KYC verification has been approved. You now have full access to all platform features.</p>
            )}
            
            {kycStatus.status === 'rejected' && kycStatus.review_notes && (
              <div>
                <p>Your KYC application was rejected. Reason:</p>
                <p style={{ color: '#ff1744', marginTop: '10px' }}>{kycStatus.review_notes}</p>
                <p style={{ marginTop: '10px' }}>You can submit a new application with corrected documents.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-box-kyc" style={{ maxWidth: '600px' }}>
        <div className="auth-header">
          <h1>KYC Verification</h1>
          <p>Complete your identity verification to unlock all features</p>
        </div>
        
        <form onSubmit={handleSubmit} className="auth-form active">
          <div className="form-group">
            <label htmlFor="document_type">Document Type</label>
            <select
              id="document_type"
              name="document_type"
              value={formData.document_type}
              onChange={handleChange}
              className="form-control"
            >
              <option value="passport">Passport</option>
              <option value="driving_license">Driving License</option>
              <option value="national_id">National ID</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="document_number">Document Number</label>
            <input
              type="text"
              id="document_number"
              name="document_number"
              value={formData.document_number}
              onChange={handleChange}
              required
              placeholder="Enter document number"
            />
          </div>
          
          <div className="form-group">
            <label>Document Front Photo *</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, 'document_front')}
              required
            />
            <FilePreview src={filePreviews.document_front} alt="Document Front Preview" />
            <small style={{ color: '#b0b8c4' }}>Clear photo of the front side of your document (Max: 5MB)</small>
          </div>
          
          <div className="form-group">
            <label>Document Back Photo</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, 'document_back')}
            />
            <FilePreview src={filePreviews.document_back} alt="Document Back Preview" />
            <small style={{ color: '#b0b8c4' }}>Required for some document types (Max: 5MB)</small>
          </div>
          
          <div className="form-group">
            <label>Selfie with Document *</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, 'selfie')}
              required
            />
            <FilePreview src={filePreviews.selfie} alt="Selfie Preview" />
            <small style={{ color: '#b0b8c4' }}>Your selfie holding the document next to your face (Max: 5MB)</small>
          </div>
          
          <button 
            type="submit" 
            className="auth-submit-btn-info"
            disabled={loading || !files.document_front || !files.selfie}
          >
            {loading ? 'Submitting...' : 'Submit KYC Application'}
          </button>
          
          {message && (
            <div style={{ 
              color: '#00c853', 
              textAlign: 'center', 
              marginTop: '10px',
              padding: '10px',
              backgroundColor: 'rgba(0, 200, 83, 0.1)',
              borderRadius: '4px'
            }}>
              {message}
            </div>
          )}
          {error && (
            <div style={{ 
              color: '#ff1744', 
              textAlign: 'center', 
              marginTop: '10px',
              padding: '10px',
              backgroundColor: 'rgba(255, 23, 68, 0.1)',
              borderRadius: '4px'
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default KYCVerification;